import {
  Add as AddIcon,
  ChevronLeft as CollapseIcon,
  Search as SearchIcon,
  SystemUpdateAlt as SystemUpdateAltIcon,
} from "@mui/icons-material";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import Fuse from "fuse.js";
import { useMemo, useState } from "react";
import { stripContextPrefix } from "../../lib/argus/argus-text";
import { RADIUS_SM } from "../../theme/theme";
import type { ArgusInstanceResponse, ArgusSessionResponse } from "../../types/argus";

/** Must match the constant in `pages/argus.tsx` so the three panel
 *  headers all sit on the same horizontal baseline. */
const HEADER_HEIGHT = 56;

export interface ArgusSessionListPanelProps {
  sessions: ArgusSessionResponse[];
  activeSessionId: string | null;
  /**
   * Preview of the first message for a session that's bootstrapping.
   */
  pendingSessionPreview?: string | null;
  /** Instance data — used to display the template version in the panel footer. */
  instance?: ArgusInstanceResponse | null;
  onSelect: (sessionId: string) => void;
  onNewSession: () => void;
  onCollapse?: () => void;
  onUpdate?: () => void;
  /** When true, the update button shows a spinner and is disabled to
   *  prevent double-submits while the boot workflow kicks off. */
  isUpdating?: boolean;
}

/**
 * Lightweight relative timestamp ("now" / "5m" / "2h" / "3d" / "Apr 9").
 * Kept in-file to avoid pulling another utility; the sidebar is the only
 * place that needs this level of density.
 */
function formatRelativeShort(iso: string): string {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return "";
  const diffMs = Date.now() - then;
  if (diffMs < 45_000) return "now";
  const minutes = Math.round(diffMs / 60_000);
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours}h`;
  const days = Math.round(hours / 24);
  if (days < 7) return `${days}d`;
  const d = new Date(then);
  return d.toLocaleString(undefined, { month: "short", day: "numeric" });
}

/**
 * Sessions panel (left column of the /argus page).
 *
 * Renders the session list with:
 *   - A compact "New" action in the header
 *   - A dense list of items showing the first-message label + a relative
 *     timestamp caption
 *   - An in-browser fuzzy search (fuse.js) over first_message text, which
 *     filters the list as you type
 *
 * Pencil: Frame 1 (`CA7pb`) left column.
 */
export const ArgusSessionListPanel = ({
  sessions,
  activeSessionId,
  pendingSessionPreview,
  instance,
  onSelect,
  onNewSession,
  onCollapse,
  onUpdate,
  isUpdating = false,
}: ArgusSessionListPanelProps) => {
  const [query, setQuery] = useState("");

  const fuse = useMemo(
    () =>
      new Fuse(sessions, {
        // `first_message` carries a `[Context: ...]\n\n` prefix from the
        // sidebar context-injection path; search against the stripped body
        // so typing "test details" doesn't get drowned out by all the
        // "[Context: Test Detail]" matches from that prefix.
        keys: [
          {
            name: "first_message",
            getFn: (s) => stripContextPrefix(s.first_message),
          },
        ],
        threshold: 0.4,
        ignoreLocation: true,
      }),
    [sessions],
  );

  const filteredSessions = useMemo(() => {
    const trimmed = query.trim();
    if (!trimmed) return sessions;
    return fuse.search(trimmed).map((result) => result.item);
  }, [query, sessions, fuse]);

  return (
    <Stack
      sx={{
        width: "100%",
        height: "100%",
        borderRight: "1px solid",
        borderColor: "divider",
      }}
    >
      <Box
        sx={{
          height: HEADER_HEIGHT,
          flexShrink: 0,
          paddingX: 1.5,
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-start",
        }}
      >
        <Button
          variant="text"
          startIcon={<AddIcon fontSize="small" />}
          onClick={onNewSession}
          size="small"
          sx={{
            textTransform: "none",
            justifyContent: "flex-start",
            fontSize: "0.8rem",
            paddingX: 1,
            color: "text.primary",
            flex: 1,
          }}
        >
          New
        </Button>
        {onCollapse && (
          <Tooltip title="Collapse sessions" placement="right">
            <IconButton size="small" onClick={onCollapse} aria-label="Collapse sessions">
              <CollapseIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </Box>
      <Box sx={{ paddingX: 1, paddingY: 1 }}>
        <TextField
          fullWidth
          size="small"
          value={query}
          placeholder="Search sessions…"
          onChange={(e) => setQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ fontSize: 16, color: "text.disabled" }} />
              </InputAdornment>
            ),
          }}
          sx={{
            "& .MuiOutlinedInput-root": {
              borderRadius: `${RADIUS_SM}px`,
              fontSize: "0.8rem",
              paddingLeft: 1,
            },
            "& .MuiOutlinedInput-input": {
              paddingY: "6px",
            },
          }}
        />
      </Box>
      <Stack
        sx={{
          flex: 1,
          overflow: "auto",
          paddingX: 1,
          paddingBottom: 2,
          gap: 0.75,
        }}
      >
        {pendingSessionPreview && (
          <Box
            sx={{
              padding: 1,
              borderRadius: `${RADIUS_SM}px`,
              backgroundColor: "action.selected",
              display: "flex",
              alignItems: "flex-start",
              gap: 1,
              textAlign: "left",
            }}
          >
            <CircularProgress size={10} sx={{ marginTop: "3px", flexShrink: 0 }} />
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography
                variant="caption"
                sx={{
                  display: "block",
                  fontWeight: 600,
                  fontSize: "0.75rem",
                  lineHeight: 1.3,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {pendingSessionPreview}
              </Typography>
              <Typography variant="caption" sx={{ color: "text.secondary", fontSize: "0.65rem" }}>
                sending…
              </Typography>
            </Box>
          </Box>
        )}
        {filteredSessions.length === 0 && !pendingSessionPreview && (
          <Typography
            variant="caption"
            sx={{
              color: "text.secondary",
              textAlign: "left",
              paddingX: 1,
              paddingTop: 1,
              fontSize: "0.7rem",
            }}
          >
            {query.trim() ? "No matches." : "No sessions yet."}
          </Typography>
        )}
        {filteredSessions.map((session) => {
          // `agent_session_id` is what the gateway's in-memory session store
          // is keyed by — the FE must use that value (not `id`, the cope-api
          // DB row UUID) for every gateway call. Fall back to `id` for
          // historical rows written before the field existed.
          const gatewayId = session.agent_session_id ?? session.id;
          const isActive = gatewayId === activeSessionId;
          const relativeTs = formatRelativeShort(session.created_at);
          return (
            <Box
              key={session.id}
              onClick={() => onSelect(gatewayId)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  onSelect(gatewayId);
                }
              }}
              sx={{
                paddingX: 1,
                paddingY: 0.5,
                borderRadius: `${RADIUS_SM}px`,
                cursor: "pointer",
                textAlign: "left",
                backgroundColor: isActive ? "action.selected" : "transparent",
                "&:hover": { backgroundColor: "action.hover" },
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                gap: 0.75,
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  fontSize: "0.75rem",
                  lineHeight: 1.3,
                  fontWeight: isActive ? 600 : 500,
                  color: "text.primary",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  flex: 1,
                  minWidth: 0,
                }}
              >
                {stripContextPrefix(session.first_message) || "Untitled session"}
              </Typography>
              {session.run_origin === "scheduled" ? (
                <Tooltip title="Scheduled background task">
                  <Chip
                    label="sched"
                    size="small"
                    variant="outlined"
                    color="warning"
                    sx={{
                      height: 16,
                      fontSize: "0.55rem",
                      fontWeight: 600,
                      "& .MuiChip-label": { px: 0.5 },
                      flexShrink: 0,
                    }}
                  />
                </Tooltip>
              ) : session.run_origin === "headless" ? (
                <Tooltip title="Headless API task">
                  <Chip
                    label="api"
                    size="small"
                    variant="outlined"
                    color="info"
                    sx={{
                      height: 16,
                      fontSize: "0.55rem",
                      fontWeight: 600,
                      "& .MuiChip-label": { px: 0.5 },
                      flexShrink: 0,
                    }}
                  />
                </Tooltip>
              ) : (session.config as { is_onboarding?: boolean } | null | undefined)
                  ?.is_onboarding === true ? (
                <Tooltip title="Onboarding session">
                  <Chip
                    label="onboarding"
                    size="small"
                    variant="outlined"
                    color="primary"
                    sx={{
                      height: 16,
                      fontSize: "0.55rem",
                      fontWeight: 600,
                      "& .MuiChip-label": { px: 0.5 },
                      flexShrink: 0,
                    }}
                  />
                </Tooltip>
              ) : null}
              <Typography
                variant="caption"
                sx={{
                  color: "text.secondary",
                  fontSize: "0.6rem",
                  lineHeight: 1,
                  flexShrink: 0,
                }}
              >
                {relativeTs}
              </Typography>
            </Box>
          );
        })}
      </Stack>
      {instance?.template_name && (
        <Box
          sx={{
            flexShrink: 0,
            paddingX: 1.5,
            paddingY: 0.75,
            borderTop: "1px solid",
            borderColor: "divider",
            display: "flex",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 0.5,
          }}
        >
          <Typography
            variant="caption"
            sx={{
              color: "text.secondary",
              fontSize: "11px",
              lineHeight: 1.2,
            }}
          >
            You are running Argus {instance.template_name.split(":").pop()}
          </Typography>
          {instance.update_available && (
            <Button
              size="small"
              variant="text"
              color="warning"
              disabled={!onUpdate || isUpdating}
              onClick={onUpdate}
              startIcon={
                isUpdating ? (
                  <CircularProgress size={12} color="inherit" />
                ) : (
                  <SystemUpdateAltIcon sx={{ fontSize: 14 }} />
                )
              }
              sx={{
                fontSize: "11px",
                lineHeight: 1.2,
                minWidth: 0,
                paddingX: 0.75,
                paddingY: 0,
                textTransform: "none",
              }}
            >
              {isUpdating ? "Updating…" : "Update available"}
            </Button>
          )}
        </Box>
      )}
    </Stack>
  );
};
